



































import Vue, { PropType } from 'vue';

import store from '@/store';

import { BasePeerGroup, WebRequestStatus } from '@/models';
import { MultiPointPacket, Badge, Objective, Outcome } from '@/models/packet';
import { OrgPeerGroup } from '@/models/orgStructure';
import { IconData } from '@/icons';
import { idGenerator } from '@/utils';

import MultiPointPacketEditor from '@/components/MultiPointPacketEditor.vue';
import PeerGroupList from '@/components/PeerGroupList.vue';

export default Vue.extend({
  components: {
    MultiPointPacketEditor,
    PeerGroupList
  },

  props: {
    packet: Object as PropType<MultiPointPacket>,
    peerGroups: Array as PropType<BasePeerGroup[]>
  },

  data() {
    return {
      tempCreateIndicatorId: idGenerator.next(),
      tempCreateObjectiveId: idGenerator.next(),
      tempUpdatePacketInfoId: idGenerator.next()
    };
  },

  computed: {
    statusedObjectives(): [Objective, WebRequestStatus][] {
      return this.packet.objectives.map(o => [
        o,
        store.state.org.activeRequests.objective.getStatus(o.objectiveId) ||
          store.getters.badges.loadStatus(o.objectiveId)
      ]);
    },

    statusedOutcomes(): [Outcome, WebRequestStatus][] {
      return this.packet.outcomes.map(o => [
        o,
        store.state.org.activeRequests.outcome.getStatus(o.outcomeId)
      ]);
    },

    statusedBadges(): [Badge, WebRequestStatus][] {
      return this.packet.objectives
        .flatMap(o => store.state.badges.badges[o.objectiveId] || [])
        .map(b => [b, store.getters.badges.updateStatus(b.badgeId)]);
    },

    inflatedGroups(): OrgPeerGroup[] {
      return this.peerGroups
        .map(pg => pg.peerGroupId)
        .map(store.getters.org.peerGroup);
    },

    createObjectiveStatus(): WebRequestStatus {
      return (
        store.state.org.activeRequests.create.getStatus(
          this.tempCreateObjectiveId
        ) || WebRequestStatus.READY
      );
    },

    createIndicatorStatus(): WebRequestStatus {
      return (
        store.state.org.activeRequests.create.getStatus(
          this.tempCreateIndicatorId
        ) || WebRequestStatus.READY
      );
    },

    basicInfoStatus(): WebRequestStatus {
      return store.state.org.requestTracker.getStatus(
        this.tempUpdatePacketInfoId
      );
    }
  },

  methods: {
    fetchBadges() {
      this.packet.objectives.forEach(({ objectiveId }) =>
        store.dispatch.badges.fetchBadges(objectiveId)
      );
    },

    createObjective(details: {
      name: string;
      description: string;
      iconData: IconData;
    }) {
      store.dispatch.org.createMultiPointGoal({
        tempCreateId: this.tempCreateObjectiveId,
        packetId: this.packet.packetId,
        name: details.name,
        description: details.description,
        iconData: details.iconData
      });
    },

    createIndicator(indicator: {
      index: number;
      value: string;
      color: string;
    }) {
      store.dispatch.org.createMultiPointIndicator({
        tempCreateId: this.tempCreateIndicatorId,
        packetId: this.packet.packetId,
        ...indicator
      });
    },

    updateObjective(objective: Objective) {
      store.dispatch.org.updatePacketObjective({
        packetId: this.packet.packetId,
        objective
      });
    },

    updateIndicator(outcome: Outcome) {
      store.dispatch.org.updatePacketOutcome({
        packetId: this.packet.packetId,
        outcome
      });
    },

    updateBadge(badge: Badge) {
      store.dispatch.badges.updateBadge(badge);
    },

    updateBasicInfo(packet: MultiPointPacket) {
      store.dispatch.org.updatePacketInfo({
        trackingId: this.tempUpdatePacketInfoId,
        packetId: packet.packetId,
        info: {
          name: packet.name,
          description: packet.description
        }
      });
    },

    deleteIndicator(outcome: Outcome) {
      store.dispatch.org.deleteOutcome({
        packetId: this.packet.packetId,
        outcomeId: outcome.outcomeId
      });
    },

    deleteObjective(objective: Objective) {
      store.dispatch.org.deleteObjective({
        packetId: this.packet.packetId,
        objectiveId: objective.objectiveId
      });
    }
  },

  watch: {
    packet(curr, prev) {
      if (curr.packetId != prev.packetId) {
        this.fetchBadges();
      }
    }
  },

  created() {
    this.fetchBadges();
  }
});
